import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { CheckCircle2, TrendingUp, Clock } from "lucide-react";
import Button from "../../../../components/ui/Button/Button";
import { servicesCTAData } from "../../../../data/services/servicesCTA";
import "./ServicesCTA.css";

gsap.registerPlugin(ScrollTrigger);

const ServicesCTA = () => {
  const sectionRef = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate left content
      gsap.fromTo(
        leftRef.current,
        { opacity: 0, x: -60 },
        {
          opacity: 1,
          x: 0,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 70%",
            toggleActions: "play none none none",
            once: true,
          },
        },
      );

      // Animate right content
      gsap.fromTo(
        rightRef.current,
        { opacity: 0, x: 60 },
        {
          opacity: 1,
          x: 0,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 70%",
            toggleActions: "play none none none",
            once: true,
          },
        },
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className="services-cta" ref={sectionRef}>
      <div className="services-cta__container">
        <div className="services-cta__content">
          {/* LEFT SIDE - Content */}
          <div className="services-cta__left" ref={leftRef}>
            <div className="services-cta__badge">
              <Clock size={14} />
              {servicesCTAData.badge}
            </div>

            <h2 className="services-cta__headline">
              {servicesCTAData.headline}
            </h2>
            <p className="services-cta__subheadline">
              {servicesCTAData.subheadline}
            </p>
            <p className="services-cta__description">
              {servicesCTAData.description}
            </p>

            {/* Stats Row */}
            <div className="services-cta__stats">
              {servicesCTAData.stats.map((stat, index) => (
                <div key={index} className="services-cta__stat">
                  <div className="services-cta__stat-number">{stat.number}</div>
                  <div className="services-cta__stat-label">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Benefits */}
            <ul className="services-cta__benefits">
              {servicesCTAData.benefits.map((benefit, index) => (
                <li key={index} className="services-cta__benefit">
                  <CheckCircle2 size={20} strokeWidth={2.5} />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>

            {/* CTA Button */}
            <div className="services-cta__action">
              <Button
                text={servicesCTAData.ctaButton.text}
                variant="primary"
                link={servicesCTAData.ctaButton.link}
              />
              <div className="services-cta__urgency">
                <TrendingUp size={16} />
                {servicesCTAData.urgency}
              </div>
            </div>
          </div>

          {/* RIGHT SIDE - Visual + Testimonial */}
          <div className="services-cta__right" ref={rightRef}>
            <div className="services-cta__visual">
              <img src={servicesCTAData.visualImage} alt="Strategy Session" />
              <div className="services-cta__visual-overlay"></div>
            </div>

            {/* Floating Testimonial Card */}
            <div className="services-cta__testimonial">
              <div className="services-cta__testimonial-quote">
                "{servicesCTAData.testimonial.quote}"
              </div>
              <div className="services-cta__testimonial-author">
                <img
                  src={servicesCTAData.testimonial.image}
                  alt={servicesCTAData.testimonial.author}
                  className="services-cta__testimonial-image"
                />
                <div className="services-cta__testimonial-info">
                  <div className="services-cta__testimonial-name">
                    {servicesCTAData.testimonial.author}
                  </div>
                  <div className="services-cta__testimonial-clinic">
                    {servicesCTAData.testimonial.clinic}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesCTA;
